import { SecuritySettings } from './securitySettings';

/**
 * The Content-Security-Policy the app applies to itself.
 *
 * Built here rather than written into `customHttp.yml` for one reason: Amplify
 * Hosting's custom headers are part of the build artefact, so a policy that broke
 * a screen could only be withdrawn by a redeploy. This one is applied from a meta
 * tag under a runtime setting, so withdrawing it is a field edit and a reload.
 *
 * A meta policy can only be enforcing — `Content-Security-Policy-Report-Only` is
 * ignored in a meta tag — and it cannot carry `frame-ancestors`, which is
 * header-only. Both of those live in `customHttp.yml` instead, where being
 * deploy-time is harmless: report-only breaks nothing, and frame-ancestors has no
 * reason to change.
 *
 * What this is actually defending against: a third-party script — the Superchat
 * widget is one, injected into our own document on every page — loading further
 * code from somewhere we never declared, posting what it reads to an arbitrary
 * host, hijacking a form, or repointing relative URLs with a base tag. What it
 * does not defend against is that script reading the DOM and localStorage of the
 * origin it was invited into. Nothing short of not inviting it does that.
 *
 * The origins are derived from `aws-exports` at runtime rather than listed here,
 * because that file is generated per environment and is not in the repository —
 * a hardcoded list would be right on exactly one environment.
 */

/** Static third-party origins, each with the thing that needs it. */
const THIRD_PARTY = {
	/**
	 * The widget loader, the CDN it pulls its own assets from, and the API its
	 * SDK fetches the workspace configuration from.
	 *
	 * `api` is not optional decoration: without it the SDK's first call fails and
	 * the widget renders a launcher that can never open a conversation. It is a
	 * separate host from the loader, which is easy to miss because the snippet
	 * Ops paste only ever names the loader.
	 */
	superchatScript: 'https://widget.superchat.de',
	superchatAssets: 'https://file-cdn.superchat.de',
	superchatApi: 'https://api.superchat.de',
	/** Google Fonts: the stylesheet in index.html, and the faces it references. */
	fontsCss: 'https://fonts.googleapis.com',
	fontsFiles: 'https://fonts.gstatic.com',
	/** Resource Hub video embeds. */
	youtube: 'https://www.youtube-nocookie.com'
} as const;

type AwsConfig = Record<string, unknown>;

const originOf = (url: string): string | null => {
	try {
		return new URL(url).origin;
	} catch {
		return null;
	}
};

/**
 * Every AWS origin this app talks to, read from the generated Amplify config.
 *
 * Deliberately precise rather than `https://*.amazonaws.com`: the wildcard would
 * cover every account in the region, including any bucket an attacker can create,
 * which is exactly the exfiltration route `connect-src` exists to close.
 */
export const amplifyConnectOrigins = (config: AwsConfig): string[] => {
	const origins = new Set<string>();

	const apis = config.aws_cloud_logic_custom;
	if (Array.isArray(apis)) {
		apis.forEach((api) => {
			const endpoint = (api as { endpoint?: string })?.endpoint;
			const origin = typeof endpoint === 'string' ? originOf(endpoint) : null;
			if (origin) {
				origins.add(origin);
			}
		});
	}

	const region = typeof config.aws_project_region === 'string' ? config.aws_project_region : '';
	const cognitoRegion = typeof config.aws_cognito_region === 'string' ? config.aws_cognito_region : region;

	if (cognitoRegion) {
		// Sign-in, token refresh, and the identity pool that signs guest requests.
		origins.add(`https://cognito-idp.${cognitoRegion}.amazonaws.com`);
		origins.add(`https://cognito-identity.${cognitoRegion}.amazonaws.com`);
	}

	if (region) {
		// RUM's dataplane — see src/rum.ts, which builds the same URL.
		origins.add(`https://dataplane.rum.${region}.amazonaws.com`);
		// Amazon Location, behind the address autocomplete.
		origins.add(`https://places.geo.${region}.amazonaws.com`);
		origins.add(`https://maps.geo.${region}.amazonaws.com`);
	}

	const bucket = config.aws_user_files_s3_bucket;
	const bucketRegion =
		typeof config.aws_user_files_s3_bucket_region === 'string' ? config.aws_user_files_s3_bucket_region : region;
	if (typeof bucket === 'string' && bucket && bucketRegion) {
		// Uploads and presigned downloads. Both addressing styles, because which
		// one a presigned URL uses is the SDK's choice, not ours.
		origins.add(`https://${bucket}.s3.${bucketRegion}.amazonaws.com`);
		origins.add(`https://s3.${bucketRegion}.amazonaws.com`);
	}

	return [...origins];
};

/** One directive per line. A thousand characters on one line is not readable. */
export const policyLines = (policy: string): string[] =>
	policy
		.split(';')
		.map((part) => part.trim())
		.filter(Boolean);

/**
 * A policy taken back apart, one row per directive.
 *
 * The inverse of building it, and it lives beside the builder for that reason.
 * The admin screen reads a policy as a table of directives and what each admits,
 * because that is the shape a person checks a host against — scanning a
 * thousand-character string for whether `font-src` names a host is how the
 * missing one went unnoticed.
 *
 * `upgrade-insecure-requests` and its kind carry no sources and come back with
 * an empty list rather than being dropped: a flag that is set is worth showing.
 */
export const parsePolicy = (policy: string): { directive: string; sources: string[] }[] =>
	policyLines(policy).map((line) => {
		const [name, ...sources] = line.split(/\s+/);
		return { directive: name, sources };
	});

const directive = (name: string, sources: (string | null | undefined)[]): string => {
	const unique = [...new Set(sources.filter((source): source is string => Boolean(source)))];
	return `${name} ${unique.join(' ')}`;
};

/**
 * The policy string for these settings and this environment.
 *
 * Where it is deliberately permissive, and why — because a policy nobody can
 * explain is a policy someone eventually turns off:
 *
 *   img-src / media-src / frame-src allow any https origin. Documents, avatars,
 *   partner logos and CDN-hosted resources come from hosts we do not enumerate,
 *   and the document preview frames presigned URLs. Locking these down needs an
 *   inventory of image and document hosts, which is its own piece of work. Note
 *   that this leaves an image beacon as a viable exfiltration channel: `img-src`
 *   is the honest gap in this policy, not an oversight.
 *
 *   style-src allows 'unsafe-inline'. Emotion — MUI's styling engine — injects
 *   style elements at runtime. A nonce would mean threading one through the
 *   emotion cache, and inline style is not a script-execution vector.
 *
 * Where it is strict, which is where the value is: script-src names its origins,
 * object-src is closed, base-uri and form-action are pinned to us.
 */
export const buildContentSecurityPolicy = (settings: SecuritySettings, config: AwsConfig): string => {
	const connect = amplifyConnectOrigins(config);
	/**
	 * Extras reach the one directive each was added to, and no other.
	 *
	 * They used to go into `script-src` and `connect-src` together, so an operator
	 * fixing a blocked image handed that host the right to run code here. Nothing
	 * announced it.
	 */
	const extra = (name: string): string[] =>
		// Tolerant of an absent list for the same reason the screen is: this is
		// called with recorded revisions, which may have been written by a version
		// of the backend that did not carry this field.
		(settings.cspExtraSources ?? []).filter((source) => source.directive === name).map((source) => source.origin);

	return [
		directive('default-src', ["'self'"]),
		// No 'unsafe-inline' and no 'unsafe-eval', and both are real restrictions
		// rather than decorative ones — but the second needs stating precisely,
		// because the bundle does contain the word. Grepping every built chunk finds
		// three sites and none of them needs to run: Prism ships regex literals that
		// merely contain "eval"; the setImmediate polyfill has a `new Function`
		// branch reached only when it is called with a string, which nothing does;
		// and pdf.js probes for eval support, which is why the viewer asks
		// `evalIsRefused()` and tells it the answer instead of letting it find out. A fourth site is a
		// Node-only `eval('require')` inside pdf.js that a browser never reaches.
		directive('script-src', ["'self'", THIRD_PARTY.superchatScript, ...extra('script-src')]),
		directive('style-src', ["'self'", "'unsafe-inline'", THIRD_PARTY.fontsCss, ...extra('style-src')]),
		// The widget serves its own typeface from the loader origin, so that origin
		// has to appear here as well as in script-src.
		directive('font-src', [
			"'self'",
			'data:',
			THIRD_PARTY.fontsFiles,
			THIRD_PARTY.superchatScript,
			...extra('font-src')
		]),
		directive('img-src', ["'self'", 'data:', 'blob:', 'https:']),
		directive('media-src', ["'self'", 'data:', 'blob:', 'https:']),
		directive('connect-src', [
			"'self'",
			...connect,
			THIRD_PARTY.superchatScript,
			THIRD_PARTY.superchatAssets,
			THIRD_PARTY.superchatApi,
			...extra('connect-src')
		]),
		// 'self' covers the about:blank frame the disclosure print view writes into;
		// blob: covers the PDF viewer; https: covers presigned document previews,
		// YouTube embeds and the isolated Superchat preview origin.
		directive('frame-src', ["'self'", 'blob:', 'data:', 'https:', THIRD_PARTY.youtube]),
		// pdf.js runs its worker from a bundled URL.
		directive('worker-src', ["'self'", 'blob:', ...extra('worker-src')]),
		directive('object-src', ["'none'"]),
		directive('base-uri', ["'self'"]),
		directive('form-action', ["'self'"]),
		'upgrade-insecure-requests'
	].join('; ');
};
